import React, {useEffect, useMemo} from "react";
import {useQuery} from "@apollo/client";
import gql from "graphql-tag";
import {useHistory} from "react-router";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import AppLayout from "../../../layouts/AppLayout";
import {Contact, ContactOrderByInput} from "../../../schema";
import {SortingRule} from "react-table";
import Table, {TableFilter} from "../../../ui/table/Table";
import H1 from "../../../ui/H1";
import {reverse} from "../../../urls";
import {AccountContext} from "../../../utils/Context";

type TableData = {
  id: string
  name: string
  type: "Person" | "Company"
  events: number
  email: string
}

function fromContact(contact: Contact): TableData {
  return {
    id: contact.id,
    name: contact.name,
    events: contact.events,
    email: contact.email,
    type: contact.type
  }
}

export default function ContactListScene() {
  const history = useHistory();

  const skipPageResetRef = React.useRef<boolean>();
  const [contacts, setContacts] = React.useState<TableData[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(1);
  const [skip, setSkip] = React.useState<number>(0);
  const [first, setFirst] = React.useState<number>(25);
  const [sortBy, setSortBy] = React.useState<ContactOrderByInput>(ContactOrderByInput.name_asc);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined);
  const [skipDataLoad, setSkipDataLoad] = React.useState<boolean>(true);

  const {data, loading, error} = useQuery<ContactQuery>(query, {
    skip: skipDataLoad,
    fetchPolicy: 'cache-and-network',
    variables: {skip, first, sortBy, searchQuery},
  });

  useMemo(() => {
    if (data) {
      setContacts(data.feed.contacts.map(x => fromContact(x)))
      setTotalCount(data.feed.total);
    }
  }, [data])

  const onFetchData = (pageIndex: number, newPageSize: number, newSortBy: SortingRule<Contact>[], filter: TableFilter | null) => {
    skipPageResetRef.current = true;
    setSkip(pageIndex * newPageSize);
    setFirst(newPageSize);
    setSearchQuery(filter?.search);
    if (newSortBy.length === 0) {
      setSortBy(ContactOrderByInput.name_asc);
    } else {
      const value = newSortBy[0];
      if (value.id === 'events') {
        if (value.desc) {
          setSortBy(ContactOrderByInput.events_desc);
        } else {
          setSortBy(ContactOrderByInput.events_asc);
        }
      } else {
        if (value.desc) {
          setSortBy(ContactOrderByInput.name_desc);
        } else {
          setSortBy(ContactOrderByInput.name_asc);
        }
      }
    }
    setSkipDataLoad(false);
  }

  const onClick = (contact: TableData) => {
    history.push(reverse('contacts_detail', {id: contact.id}));
  }

  useEffect(() => {
    skipPageResetRef.current = false;
  }, []);

  const columns = React.useMemo(() => [
    {
      Header: 'Contacts',
      columns: [
        {
          Header: 'Name',
          accessor: 'name',
          filter: 'fuzzyText',
          defaultSort: 'desc',
          sortType: 'basic',
        },
        {
          Header: 'Contact email',
          accessor: 'email',
          disableSortBy: true,
        },
        {
          Header: '# of events',
          accessor: 'events',
          sortType: 'number',
        }]
    }
  ].flatMap((c: any) => c.columns), []);

  return (
    <>
      <ReactDocumentTitle title='Contacts'/>
      <AppLayout className="">
        <AccountContext.Consumer>
          {account =>
            <div className="pt-8 pb-20">
              <H1>Contacts</H1>
              <div className="mt-6 mb-4">
                <Table<TableData, TableFilter> name="contacts" reset={skipPageResetRef.current || false}
                                               error={error?.message}
                                               loading={loading}
                                               total={totalCount}
                                               onFetchData={onFetchData} columns={columns}
                                               data={contacts}
                                               filters={[]}
                                               onClick={onClick}/>
              </div>
            </div>
          }
        </AccountContext.Consumer>
      </AppLayout>
    </>
  )
}

type ContactFeed = {
  total: number;
  count: number;
  skip: number;
  contacts: Contact[];
}
type ContactQuery = {
  feed: ContactFeed;
}


const query = gql`
  query ContactListQuery($searchQuery: String, $skip: Int!, $first: Int!, $sortBy: ContactOrderByInput!) {
    feed: contacts(searchBy: $searchQuery, orderBy: $sortBy, skip: $skip, count: $first) {
      total
      count
      skip

      contacts {
        id
        name
        email
        type
        events
      }
    },
  }
`;
